import { handleAccountInactive } from '@/auth/handleAccountInactive';
import { handleUnauthorized } from '@/auth/handleUnauthorized';
import { isUnsafeMethod, readXsrfToken } from '@/lib/csrf';
import { logger } from '@/lib/logger';

import { ApiError, extractErrorDetails, getAccountStatusCode, mapStatusToCode } from './errors';

/**
 * Fetch wrapper. Single choke point for:
 *   - Base URL (VITE_API_BASE_URL)
 *   - BFF session cookie attachment (`credentials: 'include'`)
 *   - CSRF echo (`X-XSRF-TOKEN`) on unsafe methods
 *   - Structured ApiError on non-2xx / network failure
 *   - PHI-safe structured logging (no request/response bodies logged)
 *   - 401 → full-page redirect to `/login` via `handleUnauthorized()`
 *
 * Callers MUST go through `request()` (or the verb-specific helpers) rather
 * than calling `fetch` directly. That keeps error handling and session
 * propagation uniform.
 *
 * Under BFF (PTV2-907 / PTV2-910) the FE never sees access tokens — the
 * browser attaches `JSESSIONID` automatically when `credentials: 'include'`.
 *
 * TODO(PTV2-910 follow-up): retry-on-CSRF-403. If a long-idle page POSTs
 * with a stale `XSRF-TOKEN` cookie the BE returns 403 `csrf_token_invalid`;
 * one safe `GET /api/v1/me` then a single retry of the original POST is the
 * intended remediation. See PTV2-907-auth-frontend.md note 4.
 */

const DEFAULT_TIMEOUT_MS = 30_000;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Query string parameters. `undefined` values are dropped. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Body object — serialized as JSON. Do NOT pre-stringify. */
  body?: unknown;
  /** Extra headers. `Content-Type`, `Accept`, `X-XSRF-TOKEN` are managed. */
  headers?: Record<string, string>;
  /** Abort signal from caller (React Query etc.). Merged with internal timeout. */
  signal?: AbortSignal;
  /** Override default timeout. `null` disables the timeout. */
  timeoutMs?: number | null;
  /** Suppress the global 401 → /login redirect (e.g. probing endpoints). */
  skipUnauthorizedRedirect?: boolean;
  /** Expected response type. `json` is the default; use `none` for 204 endpoints. */
  responseType?: 'json' | 'none' | 'text';
}

interface ResolvedBaseUrl {
  url: string;
}

function resolveBaseUrl(): ResolvedBaseUrl {
  const raw = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (typeof raw !== 'string' || raw.length === 0) {
    throw new ApiError({
      status: 0,
      code: 'network_error',
      message: 'VITE_API_BASE_URL is not configured.',
    });
  }
  const trimmed = raw.replace(/\/+$/, '');
  // Relative bases (`/api/v1`) target the current origin so the dev Vite
  // proxy intercepts. Absolute bases pass through unchanged.
  if (trimmed.startsWith('/') && typeof window !== 'undefined') {
    return { url: `${window.location.origin}${trimmed}` };
  }
  return { url: trimmed };
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const base = resolveBaseUrl().url;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${base}${normalizedPath}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

function mergeSignals(
  external: AbortSignal | undefined,
  timeoutMs: number | null,
): { signal: AbortSignal; cleanup: () => void } {
  const controller = new AbortController();
  const cleanups: (() => void)[] = [];

  if (external) {
    if (external.aborted) {
      controller.abort(external.reason);
    } else {
      const forward = (): void => {
        controller.abort(external.reason);
      };
      external.addEventListener('abort', forward, { once: true });
      cleanups.push(() => {
        external.removeEventListener('abort', forward);
      });
    }
  }

  if (timeoutMs !== null && timeoutMs > 0) {
    const timer = setTimeout(() => {
      controller.abort(new DOMException('Request timed out', 'TimeoutError'));
    }, timeoutMs);
    cleanups.push(() => {
      clearTimeout(timer);
    });
  }

  return {
    signal: controller.signal,
    cleanup: (): void => {
      for (const fn of cleanups) fn();
    },
  };
}

async function readBody(response: Response, responseType: RequestOptions['responseType']): Promise<unknown> {
  if (responseType === 'none') return undefined;
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return responseType === 'text' ? '' : undefined;
  }

  if (responseType === 'text') {
    return response.text();
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    const text = await response.text();
    if (text.length === 0) return undefined;
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }

  try {
    return (await response.json()) as unknown;
  } catch (cause) {
    throw new ApiError({
      status: response.status,
      code: 'invalid_response',
      message: 'Response body was not valid JSON.',
      cause,
    });
  }
}

function correlationFromHeaders(headers: Headers): string | undefined {
  return (
    headers.get('x-correlation-id') ??
    headers.get('x-request-id') ??
    headers.get('x-trace-id') ??
    undefined
  );
}

interface AbortClassification {
  aborted: boolean;
  timedOut: boolean;
}

function classifyAbort(signal: AbortSignal, err: unknown): AbortClassification {
  if (!signal.aborted) {
    const name = err instanceof Error ? err.name : '';
    if (name === 'AbortError') return { aborted: true, timedOut: false };
    if (name === 'TimeoutError') return { aborted: true, timedOut: true };
    return { aborted: false, timedOut: false };
  }
  const reason: unknown = signal.reason;
  const reasonName =
    reason instanceof Error ? reason.name : typeof reason === 'string' ? reason : '';
  const timedOut = reasonName === 'TimeoutError';
  return { aborted: true, timedOut };
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    query,
    body,
    headers: extraHeaders,
    signal: externalSignal,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    skipUnauthorizedRedirect = false,
    responseType = 'json',
  } = options;

  const url = buildUrl(path, query);
  const headers = new Headers(extraHeaders);
  // Default to JSON, but let callers override (e.g. text/plain endpoints
  // such as /system/logfile, which Spring rejects with 406 if Accept
  // doesn't match the controller's `produces`).
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  if (isUnsafeMethod(method)) {
    const csrf = readXsrfToken();
    if (csrf !== null) headers.set('X-XSRF-TOKEN', csrf);
  }

  let serializedBody: BodyInit | undefined;
  if (body !== undefined) {
    if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer) {
      serializedBody = body;
    } else {
      serializedBody = JSON.stringify(body);
      if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    }
  }

  const { signal, cleanup } = mergeSignals(externalSignal, timeoutMs);
  const startedAt = performance.now();

  const init: RequestInit = {
    method,
    headers,
    signal,
    credentials: 'include',
    mode: 'cors',
  };
  if (serializedBody !== undefined) {
    init.body = serializedBody;
  }

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (cause) {
    cleanup();
    const { aborted, timedOut } = classifyAbort(signal, cause);
    if (aborted) {
      if (timedOut) {
        logger.warn('http.request.aborted', { method, path, code: 'timeout' });
        throw new ApiError({
          status: 0,
          code: 'timeout',
          message: 'Request timed out.',
          cause,
        });
      }
      // External cancellation — react-query observer cleanup (React 19
      // StrictMode double-mount), route navigation, or an explicit caller
      // abort. Re-throw the original AbortError so react-query treats it as
      // a cancellation and does NOT cache it as a user-visible error. Wrapping
      // it as `ApiError({code: 'aborted'})` historically caused stale error
      // banners that survived successful refetches.
      logger.debug('http.request.cancelled', { method, path });
      throw cause;
    }
    logger.error('http.request.network_error', { method, path });
    throw new ApiError({
      status: 0,
      code: 'network_error',
      message: 'Network request failed.',
      cause,
    });
  }

  const durationMs = Math.round(performance.now() - startedAt);
  const correlationId = correlationFromHeaders(response.headers);

  if (!response.ok) {
    let parsed: unknown;
    try {
      parsed = await readBody(response, 'json');
    } catch (cause) {
      cleanup();
      if (cause instanceof ApiError) throw cause;
      throw new ApiError({
        status: response.status,
        code: mapStatusToCode(response.status),
        message: `HTTP ${String(response.status)}`,
        ...(correlationId !== undefined ? { correlationId } : {}),
        cause,
      });
    }
    cleanup();

    const {
      code,
      message,
      correlationId: bodyCorrelation,
      fieldViolations,
      userFacingDetail,
      contractCode,
    } = extractErrorDetails(parsed, response.status);
    const finalCorrelation = bodyCorrelation ?? correlationId;

    logger.warn('http.response.error', {
      method,
      path,
      status: response.status,
      code,
      durationMs,
      correlationId: finalCorrelation,
    });

    if (response.status === 401 && !skipUnauthorizedRedirect) {
      handleUnauthorized();
    }

    const apiError = new ApiError({
      status: response.status,
      code,
      message,
      ...(finalCorrelation !== undefined ? { correlationId: finalCorrelation } : {}),
      ...(fieldViolations !== undefined ? { fieldViolations } : {}),
      ...(userFacingDetail !== undefined ? { userFacingDetail } : {}),
      ...(contractCode !== undefined ? { contractCode } : {}),
    });

    // Defensive mid-session account-status gate (PTV2-1424): a 403 carrying an
    // `account_*` code means this user became non-ACTIVE. Refetch `/me` so the
    // SessionGuard re-renders the matching state page instead of leaving the
    // error to surface as a generic failure.
    if (getAccountStatusCode(apiError) !== null) {
      handleAccountInactive();
    }

    throw apiError;
  }

  try {
    const parsed = await readBody(response, responseType);
    cleanup();
    logger.debug('http.response.ok', {
      method,
      path,
      status: response.status,
      durationMs,
      correlationId,
    });
    return parsed as T;
  } catch (cause) {
    cleanup();
    if (cause instanceof ApiError) throw cause;
    throw new ApiError({
      status: response.status,
      code: 'invalid_response',
      message: 'Failed to parse response body.',
      ...(correlationId !== undefined ? { correlationId } : {}),
      cause,
    });
  }
}

export const http = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method'>): Promise<T> =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
